import { ref } from 'vue'
import { defineStore } from 'pinia'
import { isEmpty } from 'lodash'
import { formatISO } from 'date-fns'
import { toCamelCase } from '../utils/format'
import { useClientStore } from '@/modules/client/stores/client.store'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { ClientSetting } from '@/modules/client/types/client.enums'
import type { LocationQuery } from 'vue-router'
import type { Client, ClientView } from '@/modules/client/models/client.models'

export const useQueryStore = defineStore('query', () => {
  const clientStore = useClientStore()
  const filterStore = useFilterStore()
  const query = ref<Record<string, LocationQuery | LocationQuery[]>>({})
  const { warning } = useToaster()

  const setQuery = (queryParam: LocationQuery): void => {
    const normalized: Record<string, any> = {}
    for (const key in queryParam) {
      normalized[toCamelCase(key)] = queryParam[key]
    }
    query.value = normalized
  }

  const getClient = (userClients: Client[]): Client | undefined => {
    if (query.value.accountKey && !Array.isArray(query.value.accountKey)) {
      const clientKey = query.value.accountKey.toString()
      return userClients.find(
        (userClient) => userClient.clientKey.toLowerCase() === clientKey.toLowerCase(),
      )
    }
  }

  const getViewId = (views: ClientView[]): number | undefined => {
    if (query.value.appViewName && !Array.isArray(query.value.appViewName)) {
      const viewName = query.value.appViewName.toString()
      const view = views.find((view) => view.viewName.toLowerCase() === viewName.toLowerCase())
      if (view) {
        return view.viewId
      } else {
        warning('message.paramNotFound', { key: 'View name', param: viewName })
      }
    }
  }

  const setViewDates = (): void => {
    filterStore.filter.isViewDate = false
    if (query.value.fromViewDate && !Array.isArray(query.value.fromViewDate)) {
      const date = new Date(query.value.fromViewDate.toString())
      filterStore.filter.fromViewDate = formatISO(date)
      filterStore.filter.isViewDate = true
    }
    if (query.value.toViewDate && !Array.isArray(query.value.toViewDate)) {
      const date = new Date(query.value.toViewDate.toString())
      filterStore.filter.toViewDate = formatISO(date)
      filterStore.filter.isViewDate = true
    }
  }

  const setActionDates = (): void => {
    filterStore.filter.isActionDate = false
    if (query.value.fromActionDate && !Array.isArray(query.value.fromActionDate)) {
      const date = new Date(query.value.fromActionDate.toString())
      filterStore.filter.fromActionDate = formatISO(date)
      filterStore.filter.isActionDate = true
    }
    if (query.value.toActionDate && !Array.isArray(query.value.toActionDate)) {
      const date = new Date(query.value.toActionDate.toString())
      filterStore.filter.toActionDate = formatISO(date)
      filterStore.filter.isActionDate = true
    }
  }

  const setCarrier = (): void => {
    if (query.value.carrierName && !Array.isArray(query.value.carrierName)) {
      const carrierName = query.value.carrierName.toString()
      const carrier = clientStore.carriers.find(
        (carrier) => carrier.name.toLowerCase() === carrierName.toLowerCase(),
      )
      if (carrier) {
        filterStore.filter.carrierId = carrier.id
      } else {
        warning('message.paramNotFound', { key: 'Carrier name', param: carrierName })
      }
    }
  }

  const setCountry = (): void => {
    if (query.value.countryName && !Array.isArray(query.value.countryName)) {
      const countryName = query.value.countryName.toString()
      const country = clientStore.countries.find(
        (country) => country.name.toLowerCase() === countryName.toLowerCase(),
      )
      if (country) {
        filterStore.filter.countryId = country.id
      } else {
        warning('message.paramNotFound', { key: 'Country name', param: countryName })
      }
    }
  }

  const setServiceGroup = (): void => {
    if (query.value.serviceGroupName && !Array.isArray(query.value.serviceGroupName)) {
      const serviceGroupName = query.value.serviceGroupName.toString()
      const serviceGroup = clientStore.serviceGroups.find(
        (serviceGroup) => serviceGroup.name.toLowerCase() === serviceGroupName.toLowerCase(),
      )
      if (serviceGroup) {
        filterStore.filter.serviceGroupId = serviceGroup.id
      } else {
        warning('message.paramNotFound', { key: 'Service group name', param: serviceGroupName })
      }
    }
  }

  const setZipZone = (): void => {
    if (query.value.zipZoneName && !Array.isArray(query.value.zipZoneName)) {
      const zipZoneName = query.value.zipZoneName.toString()
      const zipZone = clientStore.zipZones.find(
        (zipZone) => zipZone.name.toLowerCase() === zipZoneName.toLowerCase(),
      )
      if (zipZone) {
        filterStore.filter.zipZoneId = zipZone.id
      } else {
        warning('message.paramNotFound', {
          key: 'Zip zone name',
          param: zipZoneName,
        })
      }
    }
  }

  const setDimensions = (dimensionValue: LocationQuery | LocationQuery[], index: number): void => {
    if (!dimensionValue || Array.isArray(dimensionValue)) {
      return
    }

    const name = dimensionValue.toString()
    const dimension = clientStore.dimensions[index]?.values.find((v) => v.value === name)

    if (!dimension) {
      warning('message.paramNotFound', { key: 'Dimension name', param: name })
      return
    }

    filterStore.filter.dimensions = {
      ...filterStore.filter.dimensions,
      [index]: dimension.id,
    }
  }

  const setAction = (): void => {
    if (query.value.actionId && !Array.isArray(query.value.actionId)) {
      const action = clientStore.actions.find(
        (action) => action.groupId.toString() === query.value.actionId.toString(),
      )
      if (action) {
        filterStore.filter.action = { ...action }
        setActionDates()
      } else {
        filterStore.filter.isViewDate = true
        warning('message.paramNotFound', { key: 'Action ID', param: query.value.actionId })
      }
    }
  }

  const setUserRelated = (): void => {
    if (query.value.myShipments) {
      filterStore.filter.isUserRelated =
        query.value.myShipments.toString() === 'true' ? true : false
    } else {
      filterStore.filter.isUserRelated = clientStore.isSettingEnabled(ClientSetting.MyShipment)
    }
  }

  const isStatusWithinRange = (statusId: number): boolean => statusId > 0 && statusId < 8

  const setStatus = (): void => {
    let status: number[] = [1, 2, 3, 4, 5, 6, 7]
    if (!query.value.status) {
      filterStore.filter.status = status
    } else if (Array.isArray(query.value.status)) {
      status = []
      query.value.status.forEach((s) => {
        const statusId = parseInt(s.toString())
        if (isStatusWithinRange(statusId)) {
          status.push(statusId)
        } else {
          warning('message.paramNotFound', { key: 'Status', param: statusId })
        }
      })
      filterStore.filter.status = status
    } else {
      const statusId = parseInt(query.value.status.toString())
      if (isStatusWithinRange(statusId)) {
        filterStore.filter.status = [statusId]
      } else {
        warning('message.paramNotFound', { key: 'Status', param: statusId })
        filterStore.filter.status = status
      }
    }
  }

  const apply = (): void => {
    if (!isEmpty(query.value)) {
      setViewDates()
      setAction()
      setCarrier()
      setCountry()
      setServiceGroup()
      setStatus()
      setUserRelated()
      setZipZone()
      setDimensions(query.value.dimension1Value, 1)
      setDimensions(query.value.dimension2Value, 2)
      setDimensions(query.value.dimension3Value, 3)
    }
  }

  return {
    query,
    setQuery,
    getClient,
    getViewId,
    apply,
  }
})
